import React from 'react';
import { FaShieldAlt, FaChartLine, FaClock } from 'react-icons/fa';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const CookiePolicy: React.FC = () => {
  const cookieTypes = [
    {
      icon: FaClock,
      name: 'Cookies Essentiels',
      description: 'Ces cookies sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés. Ils incluent les cookies de session et de sécurité.',
      required: true,
    },
    {
      icon: FaChartLine,
      name: 'Cookies Analytiques',
      description: 'Ces cookies nous permettent d\'analyser l\'utilisation du site pour améliorer nos services et votre expérience utilisateur.',
      required: false,
    },
    {
      icon: FaShieldAlt,
      name: 'Cookies de Préférences',
      description: 'Ces cookies mémorisent vos préférences et paramètres pour personnaliser votre expérience sur le site.',
      required: false,
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Politique des Cookies - Parismin',
    description: 'Informations sur l\'utilisation des cookies sur Parismin et comment gérer vos préférences.',
  };

  return (
    <Layout>
      <SEO
        title="Politique des Cookies - Parismin"
        description="Découvrez comment Parismin utilise les cookies et comment gérer vos préférences de cookies."
        url="/cookies"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-block mb-6 px-4 py-2 glass-card rounded-full text-sm font-semibold border border-yellow-500/30">
            <FaShieldAlt className="inline mr-2 text-yellow-400" />
            Cookies
          </div>
          <h1 className="text-5xl md:text-6xl font-black mb-6 heading-font">
            <span className="text-gradient">Politique des Cookies</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="glass-card rounded-3xl p-8 md:p-12 mb-12">
              <h2 className="text-3xl font-black mb-6 heading-font text-gradient">Qu'est-ce qu'un Cookie ?</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Un cookie est un petit fichier texte stocké sur votre appareil lorsque vous visitez un site web. Les cookies permettent au site de mémoriser vos actions et préférences pendant une certaine période, évitant ainsi de devoir les ressaisir à chaque visite.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Parismin utilise des cookies pour améliorer votre expérience de navigation, analyser le trafic du site et personnaliser le contenu.
              </p>
            </div>

            {/* Cookie Types */}
            <div className="space-y-6 mb-12">
              <h2 className="text-3xl font-black mb-8 heading-font text-gradient">Types de Cookies Utilisés</h2>
              {cookieTypes.map((cookie, index) => {
                const Icon = cookie.icon;
                return (
                  <div key={index} className="glass-card rounded-3xl p-8">
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="text-white text-2xl" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-2xl font-bold heading-font">{cookie.name}</h3>
                          {cookie.required && (
                            <span className="px-3 py-1 bg-red-500/20 border border-red-500/50 rounded-full text-xs font-semibold text-red-300">
                              Requis
                            </span>
                          )}
                        </div>
                        <p className="text-gray-300 leading-relaxed">{cookie.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Management */}
            <div className="glass-card rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl font-black mb-6 heading-font text-gradient">Gestion des Cookies</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Paramètres du Navigateur</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Vous pouvez contrôler et gérer les cookies via les paramètres de votre navigateur. La plupart des navigateurs vous permettent de :
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Voir quels cookies sont stockés sur votre appareil</li>
                    <li>Supprimer tous les cookies ou des cookies spécifiques</li>
                    <li>Bloquer les cookies de certains sites</li>
                    <li>Bloquer tous les cookies</li>
                    <li>Supprimer tous les cookies lors de la fermeture du navigateur</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Impact de la Désactivation</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Si vous choisissez de désactiver les cookies, certaines fonctionnalités du site peuvent ne pas fonctionner correctement. Les cookies essentiels sont nécessaires au fonctionnement de base du site.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Cookies Tiers</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Certains cookies peuvent être placés par des services tiers. Nous n'avons pas accès à ces cookies et ne pouvons pas les contrôler. Veuillez consulter les politiques de confidentialité de ces tiers pour plus d'informations.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Contact</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Pour toute question concernant notre utilisation des cookies, contactez-nous à{' '}
                    <a href="mailto:contact@parismin.com" className="text-cyan-400 hover:text-cyan-300 underline">contact@parismin.com</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CookiePolicy;

